export interface PasswordCriteria {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  specialChar: boolean;
}

export function evaluatePassword(password: string): PasswordCriteria {
  return {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    specialChar: /[^A-Za-z0-9\s]/.test(password),
  };
}

export function isPasswordValid(criteria: PasswordCriteria): boolean {
  return Object.values(criteria).every(Boolean);
}

export interface EmailCriteria {
  atSymbol: boolean;
  domain: boolean;
  noSpaces: boolean;
}

export function evaluateEmail(email: string): EmailCriteria {
  return {
    atSymbol: /@/.test(email),
    domain: /@[^\s@]+\.[^\s@]+$/.test(email),
    noSpaces: email.length > 0 && !/\s/.test(email),
  };
}

export function isEmailValid(criteria: EmailCriteria): boolean {
  return Object.values(criteria).every(Boolean);
}

const MIN_AGE_YEARS = 18;

export function getMinimumDobDate(): Date {
  const today = new Date();
  return new Date(today.getFullYear() - MIN_AGE_YEARS, today.getMonth(), today.getDate());
}

export function isOldEnough(dob: Date): boolean {
  return dob <= getMinimumDobDate();
}

// UPDATED: Included agreeAgeAndBeta to fix the TypeScript known properties compiler error
export interface SignUpFields {
  dob: Date | null;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
  agreeAnonymisedData: boolean;
  agreeAgeAndBeta: boolean; 
}

export function validateSignUpForm(fields: SignUpFields): string | null {
  const { 
    dob, 
    email, 
    password, 
    confirmPassword, 
    agreeTerms, 
    agreeAnonymisedData, 
    agreeAgeAndBeta 
  } = fields;

  if (!dob) return "Please enter your date of birth.";
  if (!isOldEnough(dob)) return "You must be at least 18 years old to sign up.";

  if (!isEmailValid(evaluateEmail(email))) return "Please enter a valid email address.";
  if (!isPasswordValid(evaluatePassword(password)))
    return "Your password does not meet the required criteria.";
  if (password !== confirmPassword) return "Passwords do not match.";

  if (!agreeTerms) return "You must agree to the terms and conditions.";
  if (!agreeAnonymisedData)
    return "You must agree to the use of your data for service improvement and statistics.";

  // UPDATED: Added safety confirmation logic to match your updated beta sign-up terms
  if (!agreeAgeAndBeta)
    return "You must confirm you are 18 or over and acknowledge the terms of the free beta application.";

  return null;
}